import React from 'react';
import { useTranslation } from 'react-i18next';
import AutoSaveInputLabel from '../../../../../../components/form/AutoSaveInputLabel';
import { FormGroup } from '../../../../../../components/form/FormGroup';
import { InputWithSave } from '../../../../../../components/form/InputsWithSave/InputWithSave';
import { SelectDropdown } from '../../../../../../components/Select/SelectDropdown';
import { GoalHint } from '../GoalHint';
import { formatPredefinedResultOptionLabel } from '../logic';
import { EmployeeObjectiveOverridesFragment } from '../../../../__generated__/graphql';
import { PendingFieldIndicator } from './PendingFieldIndicator';
import { SaveInfo, ObjectiveFormData } from './types';

type PredefinedResultOption = {
  Id: string;
  Name: string;
  flair__Result_Value__c: number | null;
  flair__Order__c: number | null;
};

type Props = {
  predefinedResultOptions: readonly PredefinedResultOption[];
  canEdit: boolean;
  value: ObjectiveFormData['objectiveResult'];
  onChange: (value: ObjectiveFormData['objectiveResult']) => void;
  saveInfo: SaveInfo;
  showResultValue?: boolean;
  overrides?: EmployeeObjectiveOverridesFragment | null;
};

const i18prefix = 'performanceReview.goals2.goalSidebar';

export const GoalResultField: React.FC<React.PropsWithChildren<Props>> = ({
  predefinedResultOptions,
  canEdit,
  value: objectiveResult,
  onChange,
  saveInfo,
  showResultValue,
  overrides,
}) => {
  const { t } = useTranslation();

  const hasPredefinedOptions = predefinedResultOptions.length > 0;

  const selectOptions = predefinedResultOptions.map((opt) => ({
    value: opt.Id,
    label: showResultValue
      ? formatPredefinedResultOptionLabel(opt.Name, opt.flair__Result_Value__c)
      : opt.Name,
  }));

  const label = t(`${i18prefix}.formFields.result`);
  const disabledHint = !canEdit ? (
    <GoalHint
      text={t(
        'performanceReview.goals2.weightedScore.resultUpdateNotAvailable',
      )}
    />
  ) : undefined;

  const pendingChanges = (
    <PendingFieldIndicator fieldName="objectiveResult" overrides={overrides} />
  );

  if (hasPredefinedOptions) {
    return (
      <FormGroup data-field="objectiveResult">
        <AutoSaveInputLabel
          label={label}
          hint={disabledHint}
          pendingChanges={pendingChanges}
          {...saveInfo}
        />
        <SelectDropdown
          value={objectiveResult?.predefinedResultOptionId ?? null}
          options={selectOptions}
          onChange={(optionId: string | null) =>
            onChange({
              predefinedResultOptionId: optionId,
              result: null,
            })
          }
          isDisabled={!canEdit}
          heightMode="large"
        />
      </FormGroup>
    );
  }

  return (
    <FormGroup data-field="objectiveResult">
      <InputWithSave
        type="number"
        value={String(objectiveResult?.result ?? '')}
        onChange={(val) =>
          onChange({
            predefinedResultOptionId: null,
            result: val !== undefined && val !== '' ? Number(val) : null,
          })
        }
        disabled={!canEdit}
        labelComponent={
          <AutoSaveInputLabel
            label={label}
            hint={disabledHint}
            pendingChanges={pendingChanges}
            {...saveInfo}
          />
        }
      />
    </FormGroup>
  );
};
